import React from "react";
import {
  Box,
  Chip,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Theme,
  Typography,
  useTheme,
} from "@mui/material";
import { Invoice } from "../../../../../models/Invoice/Invoice";
import AppTableHeaderCell from "../../../../../views/components/common/pages/AppTableHeaderCell";
import AppTablePagenation from "../../../../../views/components/common/pages/AppTablePagenation";
import { SortType } from "../../../../../models/SortType";
import { formatDate } from "../../../../../views/components/common/functions/formatDate";
import AppTableRowCell from "../../../../../views/components/common/pages/AppTableRowCell";

interface InvoicesTableProp {
  maxHeight: number;
  invoices: Invoice[];
  limit: number;
  page_no: number;
  total_pages: number;
  total_count: number;
  sortBy: string;
  sortType: SortType;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number
  ) => void;
  onRowsPerPageChange: (limit: number) => void;
  onSortChange: (column: string, sortType: SortType) => void;
}

const InvoicesTable = ({
  maxHeight,
  invoices,
  page_no,
  limit,
  total_count,
  sortBy,
  sortType,
  onPageChange,
  onRowsPerPageChange,
  onSortChange,
}: InvoicesTableProp) => {
  const theme = useTheme<Theme>();

  const handleRowsChange = (value: number) => {
    onPageChange(null, 1);
    onRowsPerPageChange(value);
  };

  const renderSortIcon = (property: string) => {
    if (sortBy === property) {
      return sortType === SortType.ASC ? "↑" : "↓";
    }
    return "↑↓";
  };

  return (
    <Paper
      sx={{
        borderRadius: "8px",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
        overflow: "hidden",
      }}
    >
      {invoices.length > 0 ? (
        <Box>
          <TableContainer sx={{ maxHeight: maxHeight }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <AppTableHeaderCell
                    sx={{
                      textAlign: "center",
                      fontSize: 14,
                      position: "sticky",
                      left: 0,
                      zIndex: 20,
                      boxShadow: "10px 0 20px -12px rgba(115,115,115,0.3);",
                      backgroundColor:
                        theme.palette.customColors?.lightGreyColor ||
                        theme.palette.grey[100],
                      minWidth: "60px",
                    }}
                  >
                    <TableSortLabel
                      active={sortBy === "id"}
                      direction={sortType === SortType.ASC ? "asc" : "desc"}
                      onClick={() =>
                        onSortChange(
                          "id",
                          sortBy === "id" && sortType === SortType.DESC
                            ? SortType.ASC
                            : SortType.DESC
                        )
                      }
                      IconComponent={() => <span>{renderSortIcon("id")}</span>}
                      sx={{ fontSize: "12px" }}
                    >
                      ID
                    </TableSortLabel>
                  </AppTableHeaderCell>
                  <AppTableHeaderCell fontSize="0.8rem">
                    <TableSortLabel
                      active={sortBy === "order_id"}
                      direction={sortType === SortType.ASC ? "asc" : "desc"}
                      onClick={() =>
                        onSortChange(
                          "order_id",
                          sortBy === "order_id" && sortType === SortType.DESC
                            ? SortType.ASC
                            : SortType.DESC
                        )
                      }
                      IconComponent={() => (
                        <span>{renderSortIcon("order_id")}</span>
                      )}
                      sx={{ fontSize: "0.8rem" }}
                    >
                      Order ID
                    </TableSortLabel>
                  </AppTableHeaderCell>
                  <AppTableHeaderCell title="Customer" fontSize="0.8rem" />
                  <AppTableHeaderCell title="Bill From" fontSize="0.8rem" />
                  <AppTableHeaderCell
                    title="Customer GSTIN"
                    fontSize="0.8rem"
                  />
                  <AppTableHeaderCell title="E-Invoice ID" fontSize="0.8rem" />
                  <AppTableHeaderCell fontSize="0.8rem">
                    <TableSortLabel
                      active={sortBy === "amount"}
                      direction={sortType === SortType.ASC ? "asc" : "desc"}
                      onClick={() =>
                        onSortChange(
                          "amount",
                          sortBy === "amount" && sortType === SortType.DESC
                            ? SortType.ASC
                            : SortType.DESC
                        )
                      }
                      IconComponent={() => (
                        <span>{renderSortIcon("amount")}</span>
                      )}
                      sx={{ fontSize: "0.8rem" }}
                    >
                      Amount
                    </TableSortLabel>
                  </AppTableHeaderCell>
                  <AppTableHeaderCell fontSize="0.8rem">
                    <TableSortLabel
                      active={sortBy === "active"}
                      direction={sortType === SortType.ASC ? "asc" : "desc"}
                      onClick={() =>
                        onSortChange(
                          "active",
                          sortBy === "active" && sortType === SortType.DESC
                            ? SortType.ASC
                            : SortType.DESC
                        )
                      }
                      IconComponent={() => (
                        <span>{renderSortIcon("active")}</span>
                      )}
                      sx={{ fontSize: "0.8rem" }}
                    >
                      Status
                    </TableSortLabel>
                  </AppTableHeaderCell>
                  <AppTableHeaderCell
                    title="Last Edited By"
                    fontSize="0.8rem"
                  />
                  <AppTableHeaderCell fontSize="0.8rem">
                    <TableSortLabel
                      active={sortBy === "date"}
                      direction={sortType === SortType.ASC ? "asc" : "desc"}
                      onClick={() =>
                        onSortChange(
                          "date",
                          sortBy === "date" && sortType === SortType.DESC
                            ? SortType.ASC
                            : SortType.DESC
                        )
                      }
                      IconComponent={() => (
                        <span>{renderSortIcon("date")}</span>
                      )}
                      sx={{ fontSize: "0.8rem" }}
                    >
                      Date
                    </TableSortLabel>
                  </AppTableHeaderCell>
                  <AppTableHeaderCell fontSize="0.8rem">
                    <TableSortLabel
                      active={sortBy === "created_date"}
                      direction={sortType === SortType.ASC ? "asc" : "desc"}
                      onClick={() =>
                        onSortChange(
                          "created_date",
                          sortBy === "created_date" &&
                            sortType === SortType.DESC
                            ? SortType.ASC
                            : SortType.DESC
                        )
                      }
                      IconComponent={() => (
                        <span>{renderSortIcon("created_date")}</span>
                      )}
                      sx={{ fontSize: "0.8rem" }}
                    >
                      Created Date
                    </TableSortLabel>
                  </AppTableHeaderCell>
                  <AppTableHeaderCell title="Modified Date" fontSize="0.8rem" />
                </TableRow>
              </TableHead>
              <TableBody>
                {invoices.map((invoice) => {
                  return (
                    <TableRow
                      key={invoice.id}
                      hover
                      sx={{
                        "&:nth-of-type(odd)": {
                          backgroundColor: theme.palette.action.hover,
                        },
                      }}
                    >
                      <AppTableRowCell
                        sx={{
                          fontSize: "12px",
                          textAlign: "center",
                          left: 0,
                          position: "sticky",
                          zIndex: 15,
                          boxShadow: "10px 0 20px -12px rgba(115,115,115,0.3);",
                          backgroundColor: theme.palette.background.paper,
                          fontWeight: 600,
                          color:
                            theme.palette.customColors?.greyTextColor ||
                            theme.palette.text.secondary,
                          minWidth: "60px",
                        }}
                      >
                        <Link
                          href={`/order/invoice/${invoice.order_id}?invoice-id=${invoice.id}`}
                          target="_blank"
                          sx={{
                            textDecoration: "underline",
                            color:
                              theme.palette.customColors?.text ||
                              theme.palette.primary.main,
                            "&:hover": {
                              textDecoration: "underline",
                            },
                          }}
                        >
                          {invoice.id}
                        </Link>
                      </AppTableRowCell>
                      <AppTableRowCell
                        sx={{ fontSize: "12px", textAlign: "center" }}
                      >
                        {invoice.order_id ? (
                          <Link
                            href={`/orders/add-orders?orderId=${invoice.order_id}`}
                            target="_blank"
                            sx={{
                              textDecoration: "underline",
                              color:
                                theme.palette.customColors?.text ||
                                theme.palette.primary.main,
                              fontWeight: 600,
                              "&:hover": {
                                textDecoration: "underline",
                              },
                            }}
                          >
                            {invoice.order_id}
                          </Link>
                        ) : (
                          "-"
                        )}
                      </AppTableRowCell>
                      <AppTableRowCell
                        sx={{ fontSize: "12px", textAlign: "center" }}
                      >
                        {invoice.customer_id && invoice.customer_name ? (
                          <Link
                            href={`/customers/add-edit-customer?customer-id=${invoice.customer_id}`}
                            target="_blank"
                            sx={{
                              textDecoration: "underline",
                              color:
                                theme.palette.customColors?.text ||
                                theme.palette.primary.main,
                              "&:hover": {
                                textDecoration: "underline",
                              },
                            }}
                          >
                            {invoice.customer_name}
                          </Link>
                        ) : (
                          invoice.customer_name || "-"
                        )}
                      </AppTableRowCell>
                      <AppTableRowCell
                        sx={{ fontSize: "12px", textAlign: "center" }}
                      >
                        {invoice.bill_from_address_gstin || "-"}
                      </AppTableRowCell>
                      <AppTableRowCell
                        sx={{ fontSize: "12px", textAlign: "center" }}
                      >
                        {invoice.gst_no || "-"}
                      </AppTableRowCell>
                      <AppTableRowCell
                        sx={{ fontSize: "12px", textAlign: "center" }}
                      >
                        {invoice.einvoice_id || "-"}
                      </AppTableRowCell>
                      <AppTableRowCell
                        sx={{ fontSize: "12px", textAlign: "center" }}
                      >
                        {invoice.amount
                          ? `₹${invoice.amount.toLocaleString("en-IN", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}`
                          : "-"}
                      </AppTableRowCell>
                      <AppTableRowCell
                        sx={{ fontSize: "12px", textAlign: "center" }}
                      >
                        {invoice.active !== undefined ? (
                          <Chip
                            label={invoice.active ? "Active" : "Cancelled"}
                            size="small"
                            color={invoice.active ? "success" : "error"}
                            variant="outlined"
                          />
                        ) : (
                          "-"
                        )}
                      </AppTableRowCell>
                      <AppTableRowCell
                        sx={{ fontSize: "12px", textAlign: "center" }}
                      >
                        {invoice.admin_name || "-"}
                      </AppTableRowCell>
                      <AppTableRowCell
                        sx={{ fontSize: "12px", textAlign: "center" }}
                      >
                        {formatDate(invoice.date)}
                      </AppTableRowCell>
                      <AppTableRowCell
                        sx={{ fontSize: "12px", textAlign: "center" }}
                      >
                        {formatDate(invoice.created_date)}
                      </AppTableRowCell>
                      <AppTableRowCell
                        sx={{ fontSize: "12px", textAlign: "center" }}
                      >
                        {formatDate(invoice.modified_date)}
                      </AppTableRowCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <AppTablePagenation
            fontSize="12px"
            pageNo={page_no}
            limit={limit}
            totalCount={total_count}
            onPageChange={onPageChange}
            handleRowsChange={handleRowsChange}
          />
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "200px",
            color: theme.palette.text.secondary,
          }}
        >
          <Typography variant="body1">No data available</Typography>
        </Box>
      )}
    </Paper>
  );
};

export default InvoicesTable;
